import { map, some } from 'lodash';
import moment from 'moment';

import {
    maintenancePriorities,
    maintenancePriorityIDs,
    conditionUnknownReason,
    maintenanceRecommendation,
    ASSET_TYPES
} from 'helpers/constants';
import { translate } from 'helpers/translateHelper';
import { sortByKey } from 'helpers/filterHelper';
import { matchesWithTheSearchFilter } from 'helpers/assetAndPowertrainFilterHelper';
import localStorageHelper from 'helpers/localStorageHelper';
import { dateFormat } from '../../PowertrainAssetDetails/DetailedInformation/AdvancedAnalyticsV2/RegreasingAdvice/constants';


export const setFilterVisibilityToLocalStorage = (visibility) => {
    localStorageHelper.setItemForUser('maintenanceOverviewFilterVisibility', visibility);
};

export const getFilterVisibilityFromLocalStorage = () => {
    const localStorageExpandedStatus = localStorageHelper.getItemForUser('maintenanceOverviewFilterVisibility');
    return localStorageExpandedStatus === null ? true : localStorageHelper.getItemForUser('maintenanceOverviewFilterVisibility');
};

// Helper function to get the correct translation key based on CBM advice and time period
const getMaintenanceTranslationKey = (baseKey, CBMAdvice, timePeriod, isMotor = false) => {
    const prefix = CBMAdvice ? 'CBM' : 'PMKit';
    if (isMotor) {
        return `ABB.Powertrain.Frontend.maintenanceOverviewAssetLubrication${baseKey}`;
    }
    return `ABB.Powertrain.Frontend.maintenanceOverviewAsset${prefix}${baseKey}${timePeriod}`;
};

// Helper function to handle MaintenanceOverdue case
const handleMaintenanceOverdue = (asset, CBMAdvice) => {
    const isMotor = ASSET_TYPES.MOTOR === asset?.assetTypeId;
    if (asset.overDueInYears) {
        return translate(getMaintenanceTranslationKey('OverDueFor', CBMAdvice, 'Years', isMotor), asset);
    }
    if (asset.overDueInMonths || asset?.overDueInMonths === 0 && asset?.overDueInYears === 0) {
        return translate(getMaintenanceTranslationKey('OverDueFor', CBMAdvice, 'Months', isMotor), asset);
    }
    return '';
};

// Helper function to handle PlanMaintenance case
const handlePlanMaintenance = (asset, CBMAdvice) => {
    const isMotor = ASSET_TYPES.MOTOR === asset?.assetTypeId;
    if (asset.dueInYears) {
        return translate(getMaintenanceTranslationKey('DueIn', CBMAdvice, 'Years', isMotor), asset);
    }
    if (asset.dueInMonths || asset?.dueInMonths === 0 && asset?.dueInYears === 0) {
        return translate(getMaintenanceTranslationKey('DueIn', CBMAdvice, 'Months', isMotor), asset);
    }
    return '';
};

// Helper function to handle Unknown reason cases
const handleUnknownReason = (asset, CBMAdvice, isMotor = false) => {
    let isCBMAvailable = true;
    let maintenanceOverviewStatus = '';

    switch (asset.reasonForUnknown) {
        case conditionUnknownReason.NoSubscription:
            maintenanceOverviewStatus = isMotor ? translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetLubricationNotSupported')
                : CBMAdvice ? translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetCBMNotSupported') : '';
            isCBMAvailable = false;
            break;
        case conditionUnknownReason.NoData:
            maintenanceOverviewStatus = isMotor ? translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetLubricationNotSupported')
                : CBMAdvice ?
                    translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetCBMNotSupported') :
                    translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetPMKitNotSupported');
            break;
        case conditionUnknownReason.NotSupported:
            maintenanceOverviewStatus = isMotor ? translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetLubricationNotSupported')
                : CBMAdvice ?
                    translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetCBMNotSupported') :
                    translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetPMKitNotSupported');
            asset?.deionizerStatus ? isCBMAvailable = true : isCBMAvailable = false;
            break;
        default:
            maintenanceOverviewStatus = translate('ABB.Powertrain.Frontend.maintenanceOverviewUnknown');
            break;
    }

    return { maintenanceOverviewStatus, isCBMAvailable };
};

/**
 * Generates a formatter function, depending on the used key for maintenance condition.
 * Used for uniform mapping asset conditions for powertrain and assetlevel.
 * @param {*} conditionKey currently used values: "condition" || "maintenanceCondition"
 * @returns asset condition mapper
 */
const getAssetConditionInfoWithKey = (conditionKey, CBMAdvice = true) => {
    return (asset) => {
        let maintenanceOverviewStatus = '';
        let isCBMAvailable = true;
        const isMotor = ASSET_TYPES.MOTOR === asset?.assetTypeId;
        const dateForLubrication = asset.nextMaintenanceDay ? moment(asset.nextMaintenanceDay).format(dateFormat) : '';
        asset.dateForLubrication = dateForLubrication;

        if (asset[conditionKey] === maintenancePriorities.MaintenanceOverdue) {
            maintenanceOverviewStatus = handleMaintenanceOverdue(asset, CBMAdvice);
        } else if (asset[conditionKey] === maintenancePriorities.PlanMaintenance) {
            maintenanceOverviewStatus = handlePlanMaintenance(asset, CBMAdvice);
        } else if (asset[conditionKey] === maintenancePriorities.Over1yToMaintenance) {
            maintenanceOverviewStatus = translate(getMaintenanceTranslationKey('DueIn', CBMAdvice, 'Years', isMotor), asset);
        } else {
            const result = handleUnknownReason(asset, CBMAdvice, isMotor);
            maintenanceOverviewStatus = result.maintenanceOverviewStatus;
            isCBMAvailable = result.isCBMAvailable;
        }

        return { maintenanceOverviewStatus, isCBMAvailable };
    };
};

export const calculateAssetsStatus = (assets) => {
    const assetsWithConditionInfo = map(assets, (asset) => {
        asset.maintenancePlan.assetTypeId = asset?.assetTypeId;
        const { maintenanceOverviewStatus: maintenanceOverviewCBMStatus, isCBMAvailable } = getAssetConditionInfoWithKey('condition', true)(asset);
        const { maintenanceOverviewStatus: maintenanceOverviewPMKitStatus } = getAssetConditionInfoWithKey('condition', false)(asset?.maintenancePlan);
        asset.maintenancePriorityID = maintenanceRecommendation[asset?.maintenancePriority]?.maintenanceID;
        asset.maintenancePriorityIconID = maintenanceRecommendation[asset?.maintenancePriority]?.iconID;
        asset.maintenanceOverviewCBMStatus = maintenanceOverviewCBMStatus;
        asset.maintenanceOverviewPMKitStatus = maintenanceOverviewPMKitStatus;
        asset.isCBMAvailable = isCBMAvailable;

        return asset;
    });

    return assetsWithConditionInfo.sort(sortByKey('componentName'));
};

const getPowertrainConditionInfo = (powertrain) => {
    let ptMaintenancePriorityID = null;
    let ptMaintenanceOverviewStatus = '';
    switch (powertrain.overallMaintenanceCondition) {
        case maintenancePriorities.MaintenanceOverdue: {
            ptMaintenancePriorityID = maintenancePriorityIDs[maintenancePriorities.MaintenanceOverdue];
            ptMaintenanceOverviewStatus = translate('ABB.Powertrain.Frontend.maintenanceOverviewExecutePreventive');
            break;
        }
        case maintenancePriorities.PlanMaintenance: {
            ptMaintenancePriorityID = maintenancePriorityIDs[maintenancePriorities.PlanMaintenance];
            if (powertrain.dueInMonths) {
                ptMaintenanceOverviewStatus = translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetDueInMonths', powertrain);
            }
            if (powertrain.dueInYears) {
                ptMaintenanceOverviewStatus = translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetDueInYears', powertrain);
            }
            break;
        }
        case maintenancePriorities.Over1yToMaintenance: {
            ptMaintenancePriorityID = maintenancePriorityIDs[maintenancePriorities.Over1yToMaintenance];
            ptMaintenanceOverviewStatus = translate('ABB.Powertrain.Frontend.maintenanceOverviewAssetDueInYears', powertrain);
            break;
        }
        case maintenancePriorities.Unknown:
        default: {
            ptMaintenancePriorityID = maintenancePriorityIDs[maintenancePriorities.Unknown];
            ptMaintenanceOverviewStatus = translate('ABB.Powertrain.Frontend.maintenanceOverviewUnknown');
            break;
        }
    }
    return { ptMaintenancePriorityID, ptMaintenanceOverviewStatus };
};

export const calculatePowertrainsStatus = (powertrains) => {

    const powertrainsWithConditionInfo = map(powertrains, (powertrain) => {
        powertrain.name = powertrain?.systemName;
        powertrain.key = `${powertrain?.systemId}`;

        powertrain.components = map(powertrain.components, (asset) => {
            asset.name = asset?.componentName;
            asset.key = `${powertrain?.systemId}-${asset.componentID}`;
            const { maintenancePriorityID, maintenanceOverviewStatus, isCBMAvailable } = getAssetConditionInfoWithKey('maintenanceCondition')(asset);
            asset.maintenancePriorityID = maintenancePriorityID;
            asset.maintenanceOverviewStatus = maintenanceOverviewStatus;
            asset.isCBMAvailable = isCBMAvailable;

            return asset;
        })
            .sort(sortByKey('componentName'))
            .sort((a, b) => b.maintenancePriorityID - a.maintenancePriorityID);

        const { ptMaintenancePriorityID, ptMaintenanceOverviewStatus } = getPowertrainConditionInfo(powertrain);
        powertrain.maintenancePriorityID = ptMaintenancePriorityID;
        powertrain.maintenanceOverviewStatus = ptMaintenanceOverviewStatus;

        return powertrain;
    });

    return powertrainsWithConditionInfo.sort(sortByKey('systemName'));

};

const isAssetFilterMatching = (asset, { priorityFilter, assetTypeFilter, searchboxFilter }) => {
    const priorityFilterMatching = priorityFilter && priorityFilter.length ? priorityFilter.indexOf(asset.maintenancePriorityID) !== -1 : true;
    const assetTypeFilterMatching = assetTypeFilter ? assetTypeFilter === asset.assetTypeId : true;
    const searchboxFilterMatching = searchboxFilter ? matchesWithTheSearchFilter(asset, searchboxFilter?.toLowerCase()) : true;

    return priorityFilterMatching &&
        assetTypeFilterMatching &&
        searchboxFilterMatching;
};

export const filterAssets = (assets, filters) => {
    return assets?.filter(asset => isAssetFilterMatching(asset, filters));
};

const isPowertrainFilterMatching = (powertrain, { priorityFilter, assetTypeFilter, searchboxFilter }) => {
    const priorityFilterMatching = priorityFilter && priorityFilter.length ?
        powertrain.components.some(asset => priorityFilter.indexOf(asset.maintenanceCondition) !== -1) : true;
    const assetTypeFilterMatching = assetTypeFilter ? some(powertrain.components, { componentType: { typeID: assetTypeFilter } }) : true;
    const searchboxFilterMatching = searchboxFilter ? matchesWithTheSearchFilter(powertrain, searchboxFilter?.toLowerCase()) ||
        powertrain.components.some(asset => matchesWithTheSearchFilter(asset, searchboxFilter?.toLowerCase())) : true;

    return priorityFilterMatching &&
        assetTypeFilterMatching &&
        searchboxFilterMatching;
};

export const filterPowertrains = (powertrains, filters) => {
    return powertrains?.filter(powertrain => isPowertrainFilterMatching(powertrain, filters));
};

const findAssetInLocation = (location, matcher) => {
    const directAsset = location?.assets?.find(matcher);
    if (directAsset) { return directAsset; }
    for (const powertrain of location?.powertrains || []) {
        const found = powertrain?.assets?.find(matcher);
        if (found) { return found; }
    }
    for (const subLocation of location?.subLocations || []) {
        const found = findAssetInLocation(subLocation, matcher);
        if (found) { return found; }
    }
    return null;
};

export const findAssetInHierarchyData = (hierarchyData, matcher) => {
    for (const site of hierarchyData?.sites || []) {
        for (const location of site?.locations || []) {
            const found = findAssetInLocation(location, matcher);
            if (found) { return found; }
        }
        const notConfigured = site?.notConfiguredAssets?.find(matcher);
        if (notConfigured) { return notConfigured; }
        for (const powertrain of site?.notConfiguredPowertrains || []) {
            const found = powertrain?.assets?.find(matcher);
            if (found) { return found; }
        }
    }
    return null;
};
